import { cn } from '@/lib/utils'
import { useRef } from 'react'

const clampPercentage = (value) => Math.min(100, Math.max(0, value))

export function calculateFocalPoint({ clientX, clientY, left, top, width, height }) {
  if (!width || !height) return { x: 50, y: 50 }

  return {
    x: clampPercentage(((clientX - left) / width) * 100),
    y: clampPercentage(((clientY - top) / height) * 100),
  }
}

export default function ImageFocalPointPicker({
  media,
  value = { x: 50, y: 50 },
  onChange,
  disabled = false,
}) {
  const markerRef = useRef(null)
  const pointerIdRef = useRef(null)

  const moveMarker = ({ x, y }) => {
    if (!markerRef.current) return

    markerRef.current.style.left = `${x}%`
    markerRef.current.style.top = `${y}%`
  }

  const getPositionFromPointer = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect()

    return calculateFocalPoint({
      clientX: event.clientX,
      clientY: event.clientY,
      left: bounds.left,
      top: bounds.top,
      width: bounds.width,
      height: bounds.height,
    })
  }

  const startSelecting = (event) => {
    if (disabled) return

    const next = getPositionFromPointer(event)
    pointerIdRef.current = event.pointerId
    event.currentTarget.dataset.dragging = 'true'
    event.currentTarget.setPointerCapture?.(event.pointerId)
    moveMarker(next)
  }

  const moveSelection = (event) => {
    if (pointerIdRef.current !== event.pointerId) return
    const next = getPositionFromPointer(event)
    moveMarker(next)
  }

  const stopSelecting = (event) => {
    if (pointerIdRef.current !== event.pointerId) return
    const next = getPositionFromPointer(event)

    pointerIdRef.current = null
    delete event.currentTarget.dataset.dragging
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    onChange?.({ x: Math.round(next.x), y: Math.round(next.y) })
  }

  const cancelSelecting = (event) => {
    if (pointerIdRef.current !== event.pointerId) return

    pointerIdRef.current = null
    delete event.currentTarget.dataset.dragging
    moveMarker(value)
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const adjustWithKeyboard = (event) => {
    if (disabled) return

    const step = event.shiftKey ? 10 : 2
    const changes = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }
    const change = changes[event.key]
    if (!change) return

    event.preventDefault()
    onChange?.({
      x: clampPercentage(value.x + change[0]),
      y: clampPercentage(value.y + change[1]),
    })
  }

  return (
    <div
      role="group"
      tabIndex={disabled ? -1 : 0}
      aria-label="Choose the image focal point"
      className={cn(
        'relative mx-auto w-fit max-w-full overflow-hidden rounded-xl border bg-muted outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        !disabled && 'cursor-crosshair data-[dragging=true]:cursor-grabbing'
      )}
      style={{ touchAction: 'none' }}
      onPointerDown={startSelecting}
      onPointerMove={moveSelection}
      onPointerUp={stopSelecting}
      onPointerCancel={cancelSelecting}
      onKeyDown={adjustWithKeyboard}
    >
      <img
        src={media.url}
        alt=""
        draggable={false}
        className="pointer-events-none block h-auto max-h-[min(60dvh,34rem)] w-auto max-w-full select-none"
      />
      <span className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10" />
      <span
        ref={markerRef}
        data-testid="image-focal-point"
        className="pointer-events-none absolute size-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary/80 shadow-[0_2px_12px_rgba(0,0,0,0.45)] ring-2 ring-primary"
        style={{ left: `${value.x}%`, top: `${value.y}%` }}
      >
        <span className="absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
      </span>
    </div>
  )
}
